"use server";

import { redirect } from "next/navigation";
import { z } from "zod";
import { isValidTimezone, DEFAULT_TIMEZONE } from "@/lib/timezones";
import { createServerSupabaseClient } from "@/lib/supabase/server";
import { createAdminClient } from "@/lib/supabase/admin";
import { claimSession } from "@/lib/session/claim-session";
import { setSessionTokenCookie } from "@/lib/session/set-session-cookie";
import { getServerTenant } from "@/lib/tenant";

function getAppBaseUrl() {
  const base = process.env.APP_BASE_URL;
  if (!base) throw new Error("Missing APP_BASE_URL.");

  // Basic safety: ensure it's a valid absolute URL.
  const u = new URL(base);

  // Optional hardening: only allow http(s)
  if (u.protocol !== "http:" && u.protocol !== "https:") {
    throw new Error("APP_BASE_URL must be http(s).");
  }

  // Normalize: no trailing slash
  return u.toString().replace(/\/$/, "");
}

const ReferralSources = [
  "None",
  "TITANSofCNC",
  "YouTube",
  "Instagram",
  "Facebook",
  "Google",
  "Tradeshow",
  "Referral",
] as const;

const SignupSchema = z
  .object({
    workspace_name: z.string().min(2).max(120),
    timezone: z.string().min(1).refine(isValidTimezone, "Invalid timezone."),
    email: z.string().email(),
    password: z.string().min(8, "Password must be at least 8 characters."),
    confirm_password: z.string().min(8),
    full_name: z.string().min(2).max(120),
    address_line1: z.string().min(2).max(200),
    address_line2: z.string().max(200).optional().or(z.literal("")),
    city: z.string().min(1).max(120),
    state: z.string().min(1).max(120),
    postal_code: z.string().min(1).max(40),
    country: z.string().min(2).max(120),

    referral_source: z.enum(ReferralSources).default("None"),
    referral_name: z.string().trim().max(24).optional().or(z.literal("")),
  })
  .refine((v) => v.password === v.confirm_password, {
    message: "Passwords do not match.",
    path: ["confirm_password"],
  })
  .refine(
    (v) =>
      v.referral_source !== "Referral" ||
      /^[A-Za-z ]{1,24}$/.test(v.referral_name ?? ""),
    {
      message: "Referral name must be letters and spaces only, max 24 characters.",
      path: ["referral_name"],
    }
  );

type ActionState = { ok: true } | { ok: false; error: string };

function t(formData: FormData, key: string) {
  const v = formData.get(key);
  return typeof v === "string" ? v.trim() : "";
}

export async function signUpAndProvision(
  _prev: ActionState,
  formData: FormData
): Promise<ActionState> {
  const parsed = SignupSchema.safeParse({
    workspace_name: t(formData, "workspace_name"),
    timezone: t(formData, "timezone") || DEFAULT_TIMEZONE,
    email: t(formData, "email").toLowerCase(),
    password: t(formData, "password"),
    confirm_password: t(formData, "confirm_password"),
    full_name: t(formData, "full_name"),
    address_line1: t(formData, "address_line1"),
    address_line2: t(formData, "address_line2"),
    city: t(formData, "city"),
    state: t(formData, "state"),
    postal_code: t(formData, "postal_code"),
    country: t(formData, "country"),
    referral_source: t(formData, "referral_source") || "None",
    referral_name: t(formData, "referral_name"),
  });

  if (!parsed.success) return { ok: false, error: parsed.error.issues[0]?.message ?? "Invalid form." };

  const input = parsed.data;

  const referralSource =
    input.referral_source === "None" ? null : input.referral_source;

  const referralName =
    input.referral_source === "Referral" ? input.referral_name?.trim() || null : null;

  const tenant = await getServerTenant();

  const supabase = await createServerSupabaseClient();
  let siteUrl: string;
  try {
    siteUrl = getAppBaseUrl();
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "Missing APP_BASE_URL." };
  }

  // Create auth user (email confirmation ON in Supabase Auth settings)
  const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
    email: input.email,
    password: input.password,
    options: {
      // Where Supabase will redirect AFTER the email link is clicked:
      emailRedirectTo: `${tenant.baseUrl}/auth/callback?next=${encodeURIComponent("/onboarding")}`,
      data: {
        full_name: input.full_name,
        pricing_config_id: tenant.pricingConfigId,
      },
    },
  });

  if (signUpError) {
    // Common: "User already registered"
    return { ok: false, error: signUpError.message };
  }

  const userId = signUpData.user?.id;
  if (!userId) return { ok: false, error: "Unable to create user." };

  // Provision workspace records using service role
  const admin = createAdminClient();

  // profile upsert (in case you already have profile triggers, this stays safe)
  const profRes = await admin
    .from("profiles")
    .upsert({ id: userId, full_name: input.full_name }, { onConflict: "id" });
  if (profRes.error) return { ok: false, error: "Could not save profile." };

    // Use organization name for the workspace name
  const desiredWorkspaceName = input.workspace_name;

  // Try to find an existing workspace that this user already owns.
  // Prefer: membership role=owner (future-proof)
  const { data: existingOwnerMem } = await admin
    .from("workspace_members")
    .select("workspace_id")
    .eq("user_id", userId)
    .eq("role", "owner")
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();

  let workspaceId: string | null = existingOwnerMem?.workspace_id ?? null;

  // Fallback: a workspace created_by this user (covers current schema nicely)
  if (!workspaceId) {
    const { data: existingCreatedWs } = await admin
      .from("workspaces")
      .select("id")
      .eq("created_by", userId)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();

    workspaceId = existingCreatedWs?.id ?? null;
  }

  // Create workspace if none exists
  if (!workspaceId) {
    const wsRes = await admin
      .from("workspaces")
      .insert({
        name: desiredWorkspaceName,
        created_by: userId,
        timezone: input.timezone,
        plan_status: "pending", // locked until webhook flips to active
        referral_source: referralSource,
        referral_name: referralName,
      })
      .select("id")
      .single();

    if (wsRes.error || !wsRes.data?.id) {
      return { ok: false, error: "Could not create workspace." };
    }

    workspaceId = wsRes.data.id as string;
  } else {
    // Ensure existing workspace is in a sane state
    // - If plan_status is null (from older path), force pending
    // - Ensure name matches the org name user just provided (optional but recommended)
    const { data: ws } = await admin
      .from("workspaces")
      .select("id, name, plan_status, referral_source, referral_name")
      .eq("id", workspaceId)
      .maybeSingle();

    if (ws?.plan_status !== "active") {
    // Force pending until Stripe activates it.
    const updatePayload: Record<string, any> = { plan_status: "pending" };

    // Optional: only set name if it's currently empty/null
    if (!ws?.name) updatePayload.name = desiredWorkspaceName;

    if (!ws?.referral_source && referralSource) {
      updatePayload.referral_source = referralSource;
      updatePayload.referral_name = referralName;
    }

    const upd = await admin
        .from("workspaces")
        .update(updatePayload)
        .eq("id", workspaceId);

    if (upd.error) return { ok: false, error: "Could not update workspace." };
    }
  }

  // Ensure membership exists (idempotent)
  const { data: existingMem } = await admin
    .from("workspace_members")
    .select("workspace_id")
    .eq("workspace_id", workspaceId)
    .eq("user_id", userId)
    .maybeSingle();

  if (!existingMem) {
    const memRes = await admin.from("workspace_members").insert({
      workspace_id: workspaceId,
      user_id: userId,
      role: "owner",
    });
    if (memRes.error) return { ok: false, error: "Could not create membership." };
  }

  // Ensure workspace settings exist (idempotent)
  const { data: existingSettings } = await admin
    .from("workspace_settings")
    .select("workspace_id")
    .eq("workspace_id", workspaceId)
    .maybeSingle();

  if (!existingSettings) {
    const settingsRes = await admin.from("workspace_settings").insert({
      workspace_id: workspaceId,
      address_line1: input.address_line1,
      address_line2: input.address_line2 || null,
      city: input.city,
      state: input.state,
      postal_code: input.postal_code,
      country: input.country,
      default_notification_email_enabled: true,
      default_notification_sms_enabled: false,
    });
    if (settingsRes.error) return { ok: false, error: "Could not save workspace settings." };
  } else {
    // Optional: if we want signup to update address fields when re-run, we can UPDATE here.
    // Keeping it no-op avoids overwriting existing org data unexpectedly.
  }


  // If confirmations are OFF (common locally), Supabase returns a session immediately.
  // If confirmations are ON (dev/prod), session will be null and user must click email link.
  if (signUpData.session) {
    const token = await claimSession(userId);
    await setSessionTokenCookie(token);
    redirect("/onboarding");
  }

  redirect(`/signup/check-email?email=${encodeURIComponent(input.email)}`);
}
